import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import showToast from '@/components/common/Toast';
import I18n from '@/modules/i18n';
import {
  plato_api_bulk_destroy_model_correlations_tags_path,
  plato_api_bulk_restore_model_correlations_tags_path,
  plato_api_model_correlation_tags_path,
  plato_api_restore_model_correlation_tags_path,
} from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import showUndoToast from '../showUndoToast';
import { correlationLabel, ModelCorrelation } from '../types';

type SendRequest = (correlation: ModelCorrelation) => Promise<unknown>;

interface ActionSpec {
  messageKey: string;
  send: SendRequest;
  undo: SendRequest;
}

// AIDEV-NOTE: Scoped by model because a page can mount several managers (the exercise builder renders
// one per question) and useIsMutating matches across the whole QueryClient — an unscoped key let a
// batch in one manager grey out the controls in every other one.
export const correlationMutationKey = (modelType: string, modelId: number) => (
  ['correlationAction', modelType, modelId]
);

// Remove and restore stay apart so an undo in flight can't put the confirm modal into its in-flight
// state, and so each can disable a different set of controls.
export const bulkRemoveMutationKey = (modelType: string, modelId: number) => (
  ['bulkCorrelationRemove', modelType, modelId]
);

export const bulkRestoreMutationKey = (modelType: string, modelId: number) => (
  ['bulkCorrelationRestore', modelType, modelId]
);

interface UseCorrelationActionsParams {
  modelId: number;
  modelType: string;
  onBulkFailure: () => void;
  onBulkUndo: () => void;
  onFailure: (correlation: ModelCorrelation) => void;
  onUndo: (correlation: ModelCorrelation) => void;
  /** Sent with every mutation so the API can refresh that program's counts. */
  programId?: number;
}

export default function useCorrelationActions({
  modelId, modelType, onBulkFailure, onBulkUndo, onFailure, onUndo, programId,
}: UseCorrelationActionsParams) {
  const queryClient = useQueryClient();
  // Omitted entirely when there is no program, so the API sees a blank param rather than "undefined".
  const programParams = programId ? { program_id: programId } : {};
  const invalidate = () => queryClient.invalidateQueries([QueryKeys.ModelCorrelations, modelType, modelId]);

  const removeMutation = useMutation({
    mutationFn: (correlation: ModelCorrelation) => axios
      .delete(plato_api_model_correlation_tags_path(correlation.id, programParams)),
    mutationKey: correlationMutationKey(modelType, modelId),
    onSuccess: invalidate,
  });

  const restoreMutation = useMutation({
    mutationFn: (correlation: ModelCorrelation) => axios
      .post(plato_api_restore_model_correlation_tags_path(correlation.id), programParams),
    mutationKey: correlationMutationKey(modelType, modelId),
    onSuccess: invalidate,
  });

  const bulkRemoveMutation = useMutation({
    mutationFn: (correlations: ModelCorrelation[]) => {
      const ids = correlations.map(({ id }) => id);

      return axios.post<{ ids: number[] }>(
        plato_api_bulk_destroy_model_correlations_tags_path(), { ids, ...programParams },
      );
    },
    mutationKey: bulkRemoveMutationKey(modelType, modelId),
    onSuccess: invalidate,
  });

  const bulkRestoreMutation = useMutation({
    mutationFn: (ids: number[]) => axios
      .post<{ ids: number[] }>(plato_api_bulk_restore_model_correlations_tags_path(), { ids, ...programParams }),
    mutationKey: bulkRestoreMutationKey(modelType, modelId),
    onSuccess: invalidate,
  });

  const { mutateAsync: sendRemove } = removeMutation;
  const { mutateAsync: sendRestore } = restoreMutation;
  const { mutateAsync: sendBulkRemove } = bulkRemoveMutation;
  const { mutateAsync: sendBulkRestore } = bulkRestoreMutation;

  const submitAction = useCallback((correlation: ModelCorrelation, { messageKey, send, undo }: ActionSpec) => {
    const standard = correlationLabel(correlation);
    const failed = () => {
      onFailure(correlation);
      showToast(I18n.t('correlation_action_error'), { msgType: 'error' });
    };
    send(correlation)
      .then(() => showUndoToast({
        announcement: I18n.t(`${messageKey}_announcement`, { standard }),
        message: I18n.t(messageKey, { standard }),
        onUndo: () => {
          onUndo(correlation);
          undo(correlation).catch(failed);
        },
        toastId: `correlation-${correlation.id}`,
      }))
      .catch(failed);
  }, [onFailure, onUndo]);

  const removeCorrelation = useCallback((correlation: ModelCorrelation) => submitAction(correlation, {
    messageKey: 'correlation_removed',
    send: sendRemove,
    undo: sendRestore,
  }), [sendRemove, sendRestore, submitAction]);

  const restoreCorrelation = useCallback((correlation: ModelCorrelation) => submitAction(correlation, {
    messageKey: 'correlation_restored',
    send: sendRestore,
    undo: sendRemove,
  }), [sendRemove, sendRestore, submitAction]);

  const batchFailed = useCallback(() => {
    onBulkFailure();
    showToast(I18n.t('correlation_action_error'), { msgType: 'error' });
  }, [onBulkFailure]);

  // Recorded before the request so the refetch lands focus on the anchor: dismissing the toast
  // removes the Undo button from under the user, and focus would otherwise fall to the document.
  const undoBatch = useCallback((ids: number[]) => {
    onBulkUndo();
    sendBulkRestore(ids)
      .then(({ data }) => { if (data.ids.length === 0) onBulkFailure(); })
      .catch(batchFailed);
  }, [batchFailed, onBulkFailure, onBulkUndo, sendBulkRestore]);

  // Resolves the ids the server actually removed, or none when the batch failed — a stale page can
  // post ids that are already gone, and the selection should only drop what really went.
  const removeCorrelations = useCallback((correlations: ModelCorrelation[]) => (
    sendBulkRemove(correlations)
      .then(({ data }) => {
        const count = data.ids.length;

        if (count === 0) {
          batchFailed();
          return data.ids;
        }

        showUndoToast({
          announcement: I18n.t('n_correlations_removed_announcement', { count }),
          message: I18n.t('n_correlations_removed', { count }),
          onUndo: () => undoBatch(data.ids),
          toastId: 'correlation-bulk',
        });

        return data.ids;
      })
      .catch(() => {
        batchFailed();
        return [] as number[];
      })
  ), [batchFailed, sendBulkRemove, undoBatch]);

  return { removeCorrelation, removeCorrelations, restoreCorrelation };
}
